import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Calculate as CalculatorIcon,
  Business as BusinessIcon,
  GetApp as DownloadIcon,
  Print as PrintIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// NJ Title Insurance Rate Schedule (2024 rates)
const njTitleRates = [
  { min: 0, max: 100000, rate: 5.75 },
  { min: 100001, max: 200000, rate: 5.25 },
  { min: 200001, max: 300000, rate: 4.75 },
  { min: 300001, max: 500000, rate: 4.25 },
  { min: 500001, max: 1000000, rate: 3.75 },
  { min: 1000001, max: 2000000, rate: 3.25 },
  { min: 2000001, max: 5000000, rate: 2.75 },
  { min: 5000001, max: Infinity, rate: 2.25 },
];

const endorsementRates = [
  { name: 'ALTA 8.1 - Environmental Protection', baseRate: 25, perThousand: 0 },
  { name: 'ALTA 9 - Restrictions, Encroachments, Minerals', baseRate: 25, perThousand: 0 },
  { name: 'ALTA 11 - Subdivision', baseRate: 50, perThousand: 0 },
  { name: 'ALTA 13 - Leasehold', baseRate: 100, perThousand: 1.0 },
  { name: 'ALTA 14 - Future Advance', baseRate: 50, perThousand: 0 },
  { name: 'ALTA 15 - Nonimputation', baseRate: 50, perThousand: 0 },
  { name: 'ALTA 17 - Access and Entry', baseRate: 25, perThousand: 0 },
  { name: 'ALTA 18 - Single Tax Parcel', baseRate: 25, perThousand: 0 },
  { name: 'ALTA 19 - Contiguity', baseRate: 50, perThousand: 0.25 },
  { name: 'ALTA 20 - First Loss', baseRate: 100, perThousand: 0.5 },
];

interface CalculationResult {
  ownerPremium: number;
  lenderPremium: number;
  simultaneousDiscount: number;
  totalEndorsements: number;
  totalPremium: number;
  breakdown: Array<{
    amount: number;
    rate: number;
    premium: number;
  }>;
}

const NJTitleRateCalculator: React.FC = () => {
  const [propertyValue, setPropertyValue] = useState<string>('');
  const [loanAmount, setLoanAmount] = useState<string>('');
  const [policyType, setPolicyType] = useState<'owner' | 'lender' | 'both'>('both');
  const [selectedEndorsements, setSelectedEndorsements] = useState<string[]>([]);
  const [calculation, setCalculation] = useState<CalculationResult | null>(null);

  const calculateOwnerPremium = (amount: number): { premium: number; breakdown: Array<{ amount: number; rate: number; premium: number }> } => {
    let remainingAmount = amount;
    let totalPremium = 0;
    const breakdown: Array<{ amount: number; rate: number; premium: number }> = [];

    for (const tier of njTitleRates) {
      if (remainingAmount <= 0) break;

      const tierAmount = Math.min(remainingAmount, tier.max - tier.min + 1);
      if (tierAmount > 0) {
        const tierPremium = (tierAmount * tier.rate) / 1000;
        totalPremium += tierPremium;
        breakdown.push({
          amount: tierAmount,
          rate: tier.rate,
          premium: tierPremium,
        });
        remainingAmount -= tierAmount;
      }
    }

    return { premium: totalPremium, breakdown };
  };

  const calculateLenderPremium = (loanAmount: number, ownerPremium: number): number => {
    // Lender's policy premium calculation (typically 25% of owner's premium for simultaneous issuance)
    const baseLenderPremium = calculateOwnerPremium(loanAmount).premium;
    return Math.max(baseLenderPremium * 0.25, 100); // Minimum $100
  };

  const calculateEndorsements = (policyAmount: number, endorsements: string[]): number => {
    let totalEndorsementFee = 0;

    endorsements.forEach(endorsementName => {
      const endorsement = endorsementRates.find(e => e.name === endorsementName);
      if (endorsement) {
        const fee = endorsement.baseRate + (policyAmount * endorsement.perThousand) / 1000;
        totalEndorsementFee += fee;
      }
    });

    return totalEndorsementFee;
  };

  const performCalculation = () => {
    const propValue = parseFloat(propertyValue.replace(/[,$]/g, '')) || 0;
    const loanAmt = parseFloat(loanAmount.replace(/[,$]/g, '')) || 0;

    if (propValue <= 0) {
      alert('Please enter a valid property value');
      return;
    }

    const ownerCalc = calculateOwnerPremium(propValue);
    const lenderPremium = loanAmt > 0 ? calculateLenderPremium(loanAmt, ownerCalc.premium) : 0;
    const simultaneousDiscount = loanAmt > 0 ? ownerCalc.premium * 0.1 : 0; // 10% discount for simultaneous issuance
    
    const endorsementAmount = policyType === 'both' ? Math.max(propValue, loanAmt) : 
                              policyType === 'owner' ? propValue : loanAmt;
    const totalEndorsements = calculateEndorsements(endorsementAmount, selectedEndorsements);

    let totalPremium = 0;
    if (policyType === 'owner') {
      totalPremium = ownerCalc.premium;
    } else if (policyType === 'lender') {
      totalPremium = lenderPremium;
    } else {
      totalPremium = ownerCalc.premium + lenderPremium - simultaneousDiscount;
    }
    totalPremium += totalEndorsements;

    setCalculation({
      ownerPremium: ownerCalc.premium,
      lenderPremium,
      simultaneousDiscount,
      totalEndorsements,
      totalPremium,
      breakdown: ownerCalc.breakdown,
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (value: string): string => {
    const num = value.replace(/[^\d.]/g, '');
    return new Intl.NumberFormat('en-US').format(parseFloat(num) || 0);
  };

  return (
    <>
      <Helmet>
        <title>NJ Title Insurance Rate Calculator | Professional Tool | MAD Title Agency</title>
        <meta
          name="description"
          content="Professional title insurance rate calculator for New Jersey. Calculate accurate premiums based on NJ Department of Banking schedules. ALTA endorsements included."
        />
        <meta
          name="keywords"
          content="NJ title insurance rates, New Jersey title calculator, title premium calculator, ALTA endorsements, professional title tools"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            NJ Title Rate Calculator
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#059669', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Professional Calculator"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                New Jersey Title Insurance Rate Calculator
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Professional tool for accurate title insurance premium calculations
              </Typography>
              <Alert severity="info" sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: 'none' }}>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Based on current NJ Department of Banking rate schedules. Rates subject to change.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <CalculatorIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Professional Tool
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Filed NJ Rate Schedule
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          {/* Calculator Input */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#059669' }}>
                Policy Information
              </Typography>
              
              <TextField
                fullWidth
                label="Property Value"
                value={propertyValue}
                onChange={(e) => setPropertyValue(formatNumber(e.target.value))}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: '$',
                }}
                helperText="Enter the full property value"
              />

              <TextField
                fullWidth
                label="Loan Amount (if applicable)"
                value={loanAmount}
                onChange={(e) => setLoanAmount(formatNumber(e.target.value))}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: '$',
                }}
                helperText="Leave blank if owner's policy only"
              />

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Policy Type</InputLabel>
                <Select
                  value={policyType}
                  label="Policy Type"
                  onChange={(e) => setPolicyType(e.target.value as 'owner' | 'lender' | 'both')}
                >
                  <MenuItem value="owner">Owner's Policy Only</MenuItem>
                  <MenuItem value="lender">Lender's Policy Only</MenuItem>
                  <MenuItem value="both">Both Policies (Simultaneous)</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                ALTA Endorsements
              </Typography>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Endorsements</InputLabel>
                <Select
                  multiple
                  value={selectedEndorsements}
                  label="Select Endorsements"
                  onChange={(e) => setSelectedEndorsements(e.target.value as string[])}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value.split(' - ')[0]} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {endorsementRates.map((endorsement) => (
                    <MenuItem key={endorsement.name} value={endorsement.name}>
                      {endorsement.name} - ${endorsement.baseRate}
                      {endorsement.perThousand > 0 && ` + $${endorsement.perThousand}/thousand`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                size="large"
                onClick={performCalculation}
                startIcon={<CalculatorIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  width: '100%',
                  py: 1.5,
                }}
              >
                Calculate Premium
              </Button>
            </Card>
          </Grid>

          {/* Results */}
          <Grid item xs={12} md={6}>
            {calculation ? (
              <Card sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#059669' }}>
                  Premium Calculation
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {policyType !== 'lender' && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body1">Owner's Policy Premium:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {formatCurrency(calculation.ownerPremium)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {policyType !== 'owner' && calculation.lenderPremium > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body1">Lender's Policy Premium:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {formatCurrency(calculation.lenderPremium)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {calculation.simultaneousDiscount > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body1" color="success.main">
                          Simultaneous Discount (10%):
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }} color="success.main">
                          -{formatCurrency(calculation.simultaneousDiscount)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {calculation.totalEndorsements > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body1">Endorsements:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {formatCurrency(calculation.totalEndorsements)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 2, backgroundColor: '#f0fdfa', px: 2, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Total Premium:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#059669' }}>
                    {formatCurrency(calculation.totalPremium)}
                  </Typography>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={() => window.print()}
                    size="small"
                  >
                    Print
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    size="small"
                  >
                    Save PDF
                  </Button>
                </Box>
              </Card>
            ) : (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Enter Policy Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fill out the form on the left to calculate title insurance premiums
                </Typography>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* Rate Schedule Table */}
        {calculation && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#059669' }}>
              Premium Breakdown by Rate Tier
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell><strong>Coverage Amount</strong></TableCell>
                    <TableCell><strong>Rate per $1,000</strong></TableCell>
                    <TableCell><strong>Premium</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calculation.breakdown.map((tier, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(tier.amount)}
                      </TableCell>
                      <TableCell>${tier.rate.toFixed(2)}</TableCell>
                      <TableCell>{formatCurrency(tier.premium)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Rate Schedule Reference */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#059669' }}>
            NJ Title Insurance Rate Schedule
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell><strong>Coverage Range</strong></TableCell>
                      <TableCell><strong>Rate per $1,000</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {njTitleRates.map((tier, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(tier.min)} - {
                            tier.max === Infinity ? 'and above' : 
                            new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(tier.max)
                          }
                        </TableCell>
                        <TableCell>${tier.rate.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={4}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Important Notes:</strong><br />
                  • Rates are per $1,000 of coverage<br />
                  • Simultaneous issuance discount: 10%<br />
                  • Minimum lender premium: $100<br />
                  • Rates subject to change by NJ DOB
                </Typography>
              </Alert>
              
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Professional Resources
                </Typography>
                <List>
                  <ListItem>
                    <Button
                      variant="outlined"
                      component={Link}
                      to="/resources/alta-compliance-checklist"
                      fullWidth
                      size="small"
                    >
                      ALTA Compliance
                    </Button>
                  </ListItem>
                  <ListItem>
                    <Button
                      variant="outlined"
                      component={Link}
                      to="/resources/title-search-manual"
                      fullWidth
                      size="small"
                    >
                      Title Search Manual
                    </Button>
                  </ListItem>
                  <ListItem>
                    <Button
                      variant="outlined"
                      component={Link}
                      to="/resources/nj-recording-requirements"
                      fullWidth
                      size="small"
                    >
                      Recording Requirements
                    </Button>
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f0fdfa', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Need Professional Title Services?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Our experienced team provides comprehensive title and closing services throughout New Jersey.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Contact Our Team
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="mailto:madtitleagency@gmail.com"
                sx={{
                  borderColor: '#059669',
                  color: '#059669',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Professional Services
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default NJTitleRateCalculator; 